import axios from "axios";
import moment from "moment";
import { ChangeBolRequest, IShipment, IShipmentArap, IShipmentExpense, IShipmentHistory, IShipmentHistoryAddRequest, IShipmentMaterial, IShipmentMaterialSearchRequest, IShipmentCommentRequest, IShipmentReportByVendorRequest, IShipmentSearchRequest, IShippedMaterial, IShipmentLight, ChangeManifestNumberRequest, AddFlagRequest } from "../types/shipment";

import { apiConfig } from "../config/authConfig";

import { CredentialManager } from "../classes/credentialManager";
import { DocumentService } from "./documentService";

export namespace ShipmentService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Shipment.
	 * @param data Shipment response data from an API.
	 **********/
	export function mapShipment(data: any) {
		let shipment: IShipment = {
			id: data.id,
			bolNumber: data.hwiNumber,
			manifestNumbers: data.manifestNumbers,
			customerId: data.customerId,
			customerName: data.customerName,
			customerClosedThroughDate: data.customerClosedThroughDate,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			transporterId: data.transporterId,
			transporterName: data.transporterName,
			plantWeightLbs: data.plantWeight,
			vendorWeightLbs: data.vendorWeight,
			pickupDate: moment(data.pickupDate).toDate(),
			scheduledPickupDate: data.scheduledPickupDate ? moment(data.scheduledPickupDate).toDate() : undefined,
			status: data.status,
			notes: data.notes,
			materials: data.materials,
			expenses: data.expenses,
			accountingDetails: data.accountingDetails,
			documents: DocumentService.mapDocuments(data.documents),
			isLegacy: data.isLegacy,
			vendorLoadId: data.vendorLoadId,
			isRedwood: data.isRedwood,
			batchId: data.batchId,
			batchStatus: data.batchStatus,
			isBatched: data.isBatched
		}

		return shipment;
	}

	/**********
	 * Convert an array of api response objects to an array of Shipments.
	 * @param data Shipment data from an API response.
	 **********/
	export function mapShipments(data: any) {
		let shipments: IShipment[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				shipments.push(mapShipment(item));
			})
		}

		return shipments;
	}

	/**********
	 * Convert one api response object to one ShipmentLight.
	 * @param data ShipmentLight response data from an API.
	 **********/
	export function mapShipmentLight(data: any) {
		let shipment: IShipmentLight = {
			id: data.id,
			loadId: data.loadId,
			hwiNumber: data.hwiNumber,
			manifestNumbers: data.manifestNumbers,
			customerId: data.customerId,
			customerName: data.customerName,
			customerClosedThroughDate: data.customerClosedThroughDate,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			transporterId: data.transporterId,
			transporterName: data.transporterName,
			plantWeight: data.plantWeight,
			vendorWeight: data.vendorWeight,
			pickupDate: moment(data.pickupDate).toDate(),
			scheduledPickupDate: data.scheduledPickupDate ? moment(data.scheduledPickupDate).toDate() : undefined,
			status: data.status,
			isLegacy: data.isLegacy,
			batchId: data.batchId,
			batchStatus: data.batchStatus,
			isBatched: data.isBatched,
			waitingFor: data.waitingFor
		}

		return shipment;
	}

	/**********
	 * Convert an array of api response objects to an array of ShipmentLights.
	 * @param data ShipmentLight data from an API response.
	 **********/
	export function mapShipmentLights(data: any) {
		let shipments: IShipmentLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				shipments.push(mapShipmentLight(item));
			})
		}

		return shipments;
	}

	/**********
	 * Convert one api response object to one Shipment.
	 * @param data Shipment response data from an API.
	 **********/
	export function mapShipmentMaterial(data: any) {
		let shipmentMaterial: IShipmentMaterial = {
			id: data.id,
			shipmentId: data.shipmentId,
			loadMaterialId: data.loadMaterialId,
			loadPosition: data.loadPosition,
			manifestId: data.manifestId,
			manifestNumber: data.manifestNumber,
			manifestVendorName: data.manifestVendorName,
			materialInventoryId: data.materialInventoryId,
			materialInventoryLocation: data.materialInventoryLocation,

			vendorMaterialPriceId: data.vendorMaterialPriceId,
			vendorMaterialPriceCategory: data.vendorMaterialPriceCategory,
			vendorMaterialPriceProfile: data.vendorMaterialPriceProfile,
			vendorMaterialPriceExternalPriceId: data.vendorMaterialPriceExternalPriceId,
			vendorMaterialPricePriceByContainer: data.vendorMaterialPricePriceByContainer,
			vendorMaterialPriceContainerId: data.vendorMaterialPriceContainerId,
			vendorMaterialPriceContainerPrice: data.vendorMaterialPriceContainerPrice,
			vendorMaterialPriceIsGrossWeightMaterial: data.vendorMaterialPriceIsGrossWeightMaterial,
			vendorAmount: data.vendorAmount,
			vendorUOMID: data.vendorUOMID,
			vendorUOMName: data.vendorUOMName,
			vendorUOMAbbreviation: data.vendorUOMAbbreviation,
			vendorMaterialId: data.vendorMaterialId,
			vendorMaterialName: data.vendorMaterialName,
			vendorMaterialIsHazardous: data.vendorMaterialIsHazardous,
			vendorMaterialCategory: data.vendorMaterialCategory,
			vendorCreditPerUnit: data.vendorCreditPerUnit,
			vendorCreditUnitName: data.vendorCreditUnitName,
			vendorCreditUnitAbbreviation: data.vendorCreditUnitAbbreviation,
			vendorCreditTotal: data.vendorCreditTotal,

			plantMaterialPriceId: data.plantMaterialPriceId,
			plantMaterialPriceCategory: data.plantMaterialPriceCategory,
			plantMaterialPriceProfile: data.plantMaterialPriceProfile,
			plantMaterialPriceExternalPriceId: data.plantMaterialPriceExternalPriceId,
			plantMaterialPricePriceByContainer: data.plantMaterialPricePriceByContainer,
			plantMaterialPriceContainerId: data.plantMaterialPriceContainerId,
			plantMaterialPriceContainerPrice: data.plantMaterialPriceContainerPrice,
			plantMaterialPriceIsGrossWeightMaterial: data.plantMaterialPriceIsGrossWeightMaterial,
			plantAmount: data.plantAmount,
			plantUOMID: data.plantUOMID,
			plantUOMName: data.plantUOMName,
			plantUOMAbbreviation: data.plantUOMAbbreviation,
			plantMaterialId: data.plantMaterialId,
			plantMaterialName: data.plantMaterialName,
			plantMaterialCategory: data.plantMaterialCategory,
			plantMaterialIsHazardous: data.plantMaterialIsHazardous,
			plantCreditPerUnit: data.plantCreditPerUnit,
			plantCreditUnitName: data.plantCreditUnitName,
			plantCreditUnitAbbreviation: data.plantCreditUnitAbbreviation,
			plantCreditTotal: data.plantCreditTotal,

			vendorCreditTotalOverridden: data.vendorCreditTotalOverridden,
			vendorCreditTotalAsCalculated: data.vendorCreditTotalAsCalculated,

			containerWeight: data.containerWeight,
			containerName: data.containerName,
			quantity: data.quantity
		}

		return shipmentMaterial;
	}

	/**********
	 * Convert an array of api response objects to an array of Shipments.
	 * @param data Shipment data from an API response.
	 **********/
	export function mapShipmentMaterials(data: any) {
		let shipmentMaterials: IShipmentMaterial[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				shipmentMaterials.push(mapShipmentMaterial(item));
			})
		}

		return shipmentMaterials;
	}

	/**********
	 * Convert one api response object to one Shipment. This is for Shipped Materials screens NOT Shipment screens!!
	 * @param data Shipment response data from an API.
	 **********/
	export function mapShippedMaterial(data: any) {
		let material: IShippedMaterial = {
			id: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			vendorId: data.vendorId,
			vendorName: data.vendorName,
			plantMaterialId: data.plantMaterialId,
			plantMaterialName: data.plantMaterialName,
			vendorMateriald: data.vendorMateriald,
			vendorMaterialName: data.vendorMaterialName,
			plantAmount: data.plantAmount,
			vendorAmount: data.vendorAmount,
			plantUnit: data.plantUnit,
			vendorUnit: data.vendorUnit,
			plantCredit: data.plantCredit,
			vendorCredit: data.vendorCredit,
			plantCreditUnit: data.plantCreditUnit,
			vendorCreditUnit: data.vendorCreditUnit,
			plantTotalCredit: data.plantTotalCredit,
			vendorTotalCredit: data.vendorTotalCredit,
		}

		return material;
	}

	/**********
	 * Convert an array of api response objects to an array of Shipments. This is for Shipped Materials screens NOT Shipment screens!!
	 * @param data Shipment data from an API response.
	 **********/
	export function mapShippedMaterials(data: any) {
		let materials: IShipmentMaterial[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				materials.push(mapShippedMaterial(item));
			})
		}

		return materials;
	}

	/**********
	 * Convert one api response object to one Shipment history comment.
	 * @param data ShipmentHistory response data from an API.
	 **********/
	export function mapShipmentHistory(data: any): IShipmentHistory {
		let history: IShipmentHistory = {
			id: data.id,
			shipmentId: data.shipmentId,
			type: data.type,
			username: data.username,
			description: data.description,
			date: new Date(data.date),
			oldStatus: data.oldStatus,
			newStatus: data.newStatus,
			category: data.category,
			action: data.action,
			misc: data.misc
		};

		return history;
	}

	/**********
	 * Convert an array of api response objects to an array of Shipment history comments.
	 * @param data ShipmentHistory data from an API response.
	 **********/
	export function mapShipmentHistories(data: any): IShipmentHistory[] {
		let comments: IShipmentHistory[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				comments.push(mapShipmentHistory(item));
			})
		}

		return comments;
	}

	/**********
	 * Gets all shipments.
	 **********/
	export async function getShipments(start: Date, end: Date) {
		const s = String(start.getFullYear()) + String(start.getMonth() + 1).padStart(2, '0') + String(start.getDate()).padStart(2, '0');
		const e = String(end.getFullYear()) + String(end.getMonth() + 1).padStart(2, '0') + String(end.getDate()).padStart(2, '0');

		const apiResponse = axios.get(
			apiConfig.default + "/shipment/all/" + s + "/" + e,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a shipment.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipment(shipmentId: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + shipmentId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a shipment.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipmentLight(shipmentId: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + shipmentId + "/details",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a shipment's materials.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipmentMaterials(shipmentId: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + shipmentId + "/materials",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets shipment materials for all provided shipment IDs.
	 * @param shipmentIds The IDs of the shipments.
	 **********/
	export async function getMultipleShipmentsMaterials(shipmentIds: number[]) {
		const response = axios.post(
			apiConfig.default + "/shipment/multiple/materials",
			{ ids: shipmentIds },
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a shipment's expenses.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipmentExpenses(shipmentId: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + shipmentId + "/expenses",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a shipment expense payee options for the provided customer.
	 * @param customerId The id of the shipment.
	 **********/
	export async function getShipmentExpensePayees(customerId: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + customerId + "/expenses/payees",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets a shipment's AR AP info.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipmentArap(shipmentId: number) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + shipmentId + "/arap",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets documents associated with shipment.
	 * @param shipmentId The id of the shipment.
	 **********/
	export async function getShipmentDocuments(shipmentId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/shipment/" + shipmentId + "/documents",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all shipped materials for a date range.
	 **********/
	export async function getShippedMaterials(start: Date, end: Date) {
		const s = String(start.getFullYear()) + String(start.getMonth() + 1).padStart(2, '0') + String(start.getDate()).padStart(2, '0');
		const e = String(end.getFullYear()) + String(end.getMonth() + 1).padStart(2, '0') + String(end.getDate()).padStart(2, '0');

		const apiResponse = axios.get(
			apiConfig.default + "/shipment/materials/" + s + "/" + e,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets lists of values used for filtering the Shipment Search.
	 **********/
	export async function getSearchValueSets() {
		const response = axios.get(
			apiConfig.default + "/shipment/search/valuesets",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Searches shipments by provided criteria.
	 * @param request The search request criteria.
	 **********/
	export async function searchShipments(request: IShipmentSearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/shipment/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Searches shipped materials by provided criteria.
	 * @param request The search request criteria.
	 **********/
	export async function searchShippedMaterials(request: IShipmentMaterialSearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/shipment/materials/search",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Adds a vendor only material to an existing shipment.
	 * @param material The material to add
	 **********/
	export async function addMaterialToShipment(material: IShipmentMaterial) {

		const apiResponse = axios.put(
			apiConfig.default + "/shipment/materials/add",
			material,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add vendor material id and amount to a shipment detail record.
	 * @param shipmentArap shipment accounting fields.
	 **********/
	export async function updateVendorsMaterialInfo(request: IShipmentMaterial) {
		const apiResponse = axios.put(
			apiConfig.default + "/shipment/vendormaterial/",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add accounting info to a shipment.
	 * @param shipmentArap shipment accounting fields.
	 **********/
	export async function setArap(id: number, arap: IShipmentArap) {
		const apiResponse = axios.put(
			apiConfig.default + "/shipment/" + id + "/arap",
			arap,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add an expense to a shipment.
	 * @param id shipment id.
	 * @param data a shipment expense object.
	 **********/
	export async function addExpense(id: string, data: IShipmentExpense) {
		const apiResponse = axios.put(
			apiConfig.default + "/shipment/" + id + "/expense",
			data,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove an expense from a shipment.
	 * @param id expense id.
	 **********/
	export async function removeExpense(id: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/shipment/expense/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a material from a shipment.
	 * @param id detail record id.
	 **********/
	export async function removeMaterial(id: number) {
		const apiResponse = axios.delete(
			apiConfig.default + "/shipment/detail/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a shipment's status.
	 * @param id status id.
	 **********/
	export async function getStatus(id: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + id + "/status",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusReconciling(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/reconciling",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusReviewing(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/reviewing",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusEscalated(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/escalated",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusAuditing(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/auditing",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusQBEntry(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/qbentry",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusQBAudit(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/qbaudit",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's status.
	 * @param id status id.
	 **********/
	export async function setStatusComplete(id: string, request?: IShipmentCommentRequest) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/complete",
			request ? request : "payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Searches shipped materials by provided criteria.
	 * @param request The search request criteria.
	 **********/
	export async function reportByVendor(request: IShipmentReportByVendorRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/shipment/report/byvendor",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Deletes a shipment, it's details, it's load, and related details records.
	 * @param id shipment id.
	 **********/
	export async function deleteShipment(id: string, reason: string) {
		const payload = {
			shipmentId: id,
			deletedReason: reason
		}

		const response = axios.post(			
			apiConfig.default + "/shipment/",
			payload,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's pickup date.
	 * @param id shipment id.
	 * @param newDate The new pickup date.
	 **********/
	export async function changeDate(id: string, newDate) {
		const d = String(newDate.getFullYear()) + String(newDate.getMonth() + 1).padStart(2, '0') + String(newDate.getDate()).padStart(2, '0');

		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/date/" + d,
			"fake payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's BOL (HWI) number.
	 * @param id The shipment id.
	 * @param newBol The new BOL number.
	 **********/
	export async function changeBol(id: string, newBol: string) {
		const request: ChangeBolRequest = { bolNumber: newBol };
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/bol",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment material's manifest number.
	 * @param shipmentMaterialId The shipment material id.
	 * @param newManifestNumber The new manifest number.
	 **********/
	export async function changeManifestNumber(shipmentMaterialId: string, newManifestNumber: string) {
		const request: ChangeManifestNumberRequest = { manifestNumber: newManifestNumber };
		const response = axios.put(
			apiConfig.default + "/shipment/detail/" + shipmentMaterialId + "/manifestNumber",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Flags a shipment as waiting.
	 **********/
	export async function addFlag(id: number, reason: string) {
		const request: AddFlagRequest = { reason: reason };
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/flag",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Removes the waiting flag from a shipment.
	 **********/
	export async function removeFlag(id: number) {
		const response = axios.put(
			apiConfig.default + "/shipment/" + id + "/unflag",
			"payload",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Sets a shipment's vendor and/or transporter IDs.
	 * @param id The shipment id.
	 * @param vendorId The new vendor id. Send 0 if not updating the vendor.
	 * @param transporterId The new transporter id. Send 0 if not updating the transporter.
	 **********/
	export async function changeVendorAndTransporterIds(id: string, vendorId: string, transporterId: string) {
		if (isNaN(parseInt(vendorId))) vendorId = "0";
		if (isNaN(parseInt(transporterId))) transporterId = "0";
		const response = axios.patch(
			apiConfig.default + "/shipment/" + id + "/vendor/" + vendorId + "/transporter/" + transporterId,
			null,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets comments from a shipment's change history.
	 * @param id The shipment id.
	 **********/
	export async function getShipmentHistory(id: string) {
		const response = axios.get(
			apiConfig.default + "/shipment/" + id + "/history",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Adds a comment to the shipment's change history.
	 * @param request The comment to add.
	 **********/
	export async function addHistoryComment(id: string, request: IShipmentHistoryAddRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/shipment/" + id + "/history",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets the number of shipments in each workflow status for a month and year.
	 * @param month
	 * @param year
	 **********/
	export async function getClosingStatus(month: number, year: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/shipment/closing-status/" + year + "/" + month,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets data necessary for generating a pink sheet.
	 * @param shipmentIds The IDs of the shipments.
	 **********/
	export async function getPinkSheetData(shipmentIds: number[]) {
		const apiResponse = axios.post(
			apiConfig.default + `/shipment/pinksheet/data`,
			{ shipmentIds },
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a list of all shipments with waiting flags
	 **********/
	export async function getWaitingShipments() {
		const response = axios.get(
			apiConfig.default + "/shipment/waiting",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Exports a list of all shipments with waiting flags
	 **********/
	export async function exportWaitingShipments() {
		const response = axios.get(
			apiConfig.default + "/shipment/waiting/export",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Searches shipments by provided criteria and exports the results to a CSV file.
	 * @param request The search request criteria.
	 **********/
	export async function exportShipmentList(request: IShipmentSearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/shipment/export",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
 	 * Get a list of warnings related to manifest numbers for a specified shipment by ID.
	 * @param id The ID of the shipment for which to retrieve manifest warnings.
 	 **********/
	export async function getManifestWarningsByShipment(id: number) {
		const response = axios.get(
			apiConfig.default + `/shipment/${id}/manifest/warnings`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}
}
